'use client'

import React, { useState } from 'react'
import { Link } from '@/lib'
import Components from '../components'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Play, Image, FileText, Users, Target, Lightbulb, TrendUp, Quotes, ArrowRight, Calendar, Clock, User, Brain, Lightning, CheckCircle, ChartBar, Database, ShieldCheck, Stack } from 'phosphor-react'

export default function AIAgentCaseStudy() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  return (
    <Components.PageContainer>
      <Components.GlassNavigation />
      
      <main className="pt-16 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Hero Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-6xl font-bold text-[var(--brutal-ink)] mb-4 leading-tight">
                AI Agent
              </h1>
              <h2 className="text-2xl md:text-3xl text-[var(--brutal-muted)] mb-8 leading-relaxed">
                Retail Promotion Planning
              </h2>
              <p className="text-xl md:text-2xl text-[var(--brutal-muted)] mb-8 max-w-4xl mx-auto leading-relaxed">
                From insights to AI-powered design: Building a strategic vision for agentic AI in enterprise planning.
              </p>
              
              <div className="flex flex-wrap justify-center gap-3 mb-12">
                <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">AI Strategy</Badge>
                <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">UX Leadership</Badge>
                <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Cross-functional Alignment</Badge>
                <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Agentic AI</Badge>
                <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Design Thinking</Badge>
              </div>
            </div>
            
            {/* Hero Video */}
            <Card variant="media" className="brutal-panel mb-8 overflow-hidden">
              <Components.VideoModal
                dataPath="videos/AI-agent-create-promotion.mov"
                alt="AI-powered dashboard visualization"
                poster="/assets/ai-agent-card.png"
                className="w-full"
                requireAuth={false}
              />
            </Card>
          </section>

          {/* Challenge / Context */}
          <section className="mb-20">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <h2 className="text-4xl font-bold text-[var(--brutal-ink)] mb-6">The Challenge</h2>
                <p className="text-[var(--brutal-muted)] text-lg leading-relaxed mb-6">
                  LLMs and agentic AI were moving fast, but we didn&apos;t have a shared way to turn planner pain into AI work that was safe to ship. Product, customer success, data science, and engineering each had a different picture of what &ldquo;an AI agent&rdquo; meant.
                </p>
                <p className="text-[var(--brutal-muted)] text-lg leading-relaxed">
                  The hard part was judgment under pressure: where to spend the next quarter when every option looked plausible and none were free.
                </p>
              </div>

              <div>
                <Card variant="media" className="brutal-panel overflow-hidden">
                  <Components.ImageDisplay
                    src="/assets/advisor-research.png"
                    alt="Workshop Overview - Cross-functional alignment sessions"
                    className="w-full h-full object-cover"
                    style={{ filter: 'contrast(1.15) saturate(1.1) brightness(1.05)' }}
                  />
                </Card>
              </div>
            </div>

            <Components.KeyDecisions
              className="!mb-0"
              decisions={[
                {
                  question: 'Ship a chat UI now, or align on the job first?',
                  tension:
                    'A polished chatbot would have looked like progress. It also would have locked us into the wrong autonomy model before we agreed what the agent was allowed to change.',
                  choice:
                    'We ran workshops and built a concept car first, so the roadmap described a job instead of a chat widget.',
                },
                {
                  question: 'Fine-tune everything, or retrieve living enterprise data?',
                  tension:
                    'Fine-tuning looked decisive. Promo calendars and retailer rules change weekly, and model weights do not keep up with that pace.',
                  choice:
                    'We went retrieval-first, and only fine-tuned when the behavior truly had to live in the model.',
                },
                {
                  question: 'Full autonomy, or supervised delegation?',
                  tension:
                    'A fully autonomous agent demoed well. High-stakes promo edits do not forgive fluent mistakes.',
                  choice:
                    'We designed suggest, confirm, and act levels, with a human gate wherever the risk was not reversible.',
                },
              ]}
            />
          </section>

          {/* Process / Actions - Timeline */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-[var(--brutal-ink)] mb-12 text-center">Strategic Process</h2>
            
            <div className="space-y-8">
              {/* Step 1 */}
              <Card className="brutal-panel">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-3 gap-8 items-center">
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 brutal-chip bg-[var(--brutal-mustard)] font-bold text-sm">
                          1
                        </div>
                        <h3 className="text-2xl font-bold text-[var(--brutal-ink)] break-words">Virtual Workshop Facilitation</h3>
                      </div>
                      <p className="text-[var(--brutal-muted)] text-lg leading-relaxed break-words">
                        Hosted cross-functional workshops bringing together product, customer success, data science, and engineering teams. 
                        Facilitated collaborative sessions to identify pain points and opportunities for AI integration.
                      </p>
                    </div>
                    <div className="rounded-lg border border-[var(--brutal-ink)] overflow-hidden h-52">
                      <img 
                        src="/assets/Design-Workshop-JBP.png" 
                        alt="Cross-functional workshop session"
                        className="w-full h-full object-cover"
                        style={{ imageRendering: 'crisp-edges', filter: 'contrast(1.1) saturate(1.1)' }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Step 2 */}
              <Card className="brutal-panel">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-3 gap-8 items-center">
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 brutal-chip bg-[var(--brutal-mustard)] font-bold text-sm">
                          2
                        </div>
                        <h3 className="text-2xl font-bold text-[var(--brutal-ink)] break-words">Problem Statement Development</h3>
                      </div>
                      <p className="text-[var(--brutal-muted)] text-lg leading-relaxed break-words">
                        Synthesized workshop insights to identify core problem statements and strategic opportunities. 
                        Mapped user pain points to potential AI solutions with clear business value propositions.
                      </p>
                    </div>
                    <div className="rounded-lg border border-[var(--brutal-ink)] overflow-hidden h-52">
                      <img 
                        src="/assets/ai-agent-problem-statement.png" 
                        alt="Problem statement development"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Step 3 */}
              <Card className="brutal-panel">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-3 gap-8 items-center">
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 brutal-chip bg-[var(--brutal-mustard)] font-bold text-sm">
                          3
                        </div>
                        <h3 className="text-2xl font-bold text-[var(--brutal-ink)] break-words">Concept Car Development</h3>
                      </div>
                      <p className="text-[var(--brutal-muted)] text-lg leading-relaxed break-words">
                        Created a concept car version of agentic AI that demonstrated the vision in tangible terms. 
                        Developed interactive prototypes showing how AI agents could guide complex planning workflows.
                      </p>
                    </div>
                    <div className="rounded-lg border border-[var(--brutal-ink)] overflow-hidden h-52">
                      <img 
                        src="/assets/ai-agent-ideation.png" 
                        alt="AI solution ideation and brainstorming"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Step 4 */}
              <Card className="brutal-panel">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-3 gap-8 items-center">
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 brutal-chip bg-[#f4a6b8] font-bold text-sm">
                          4
                        </div>
                        <h3 className="text-2xl font-bold text-[var(--brutal-ink)] break-words">Competitive Analysis & Research</h3>
                      </div>
                      <p className="text-[var(--brutal-muted)] text-lg leading-relaxed break-words">
                        Conducted comprehensive competitive analysis and internal tool exploration. 
                        Researched training and tuning approaches to inform our AI strategy with data-driven insights.
                      </p>
                    </div>
                    <div className="rounded-lg border border-[var(--brutal-ink)] overflow-hidden h-52">
                      <img 
                        src="/assets/ai-agent-validation.png" 
                        alt="User validation and testing"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Step 5 */}
              <Card className="brutal-panel">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-3 gap-8 items-center">
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 brutal-chip bg-[#8fce8f] font-bold text-sm">
                          5
                        </div>
                        <h3 className="text-2xl font-bold text-[var(--brutal-ink)] break-words">Research-Driven Training Exploration</h3>
                      </div>
                      <p className="text-[var(--brutal-muted)] text-lg leading-relaxed break-words">
                        Explored research-driven training and tuning methodologies for AI models. 
                        Collaborated with data science teams to understand technical constraints and opportunities.
                      </p>
                    </div>
                  <div className="rounded-lg border border-[var(--brutal-ink)] overflow-hidden h-52">
                      <img 
                        src="/assets/ai-fine-tuning.png" 
                        alt="AI architecture and system design"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Step 6 */}
              <Card className="brutal-panel">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-3 gap-8 items-center">
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 brutal-chip bg-[var(--brutal-accent)] font-bold text-sm">
                          6
                        </div>
                        <h3 className="text-2xl font-bold text-[var(--brutal-ink)]">Design Thinking Applied to Roadmap</h3>
                      </div>
                      <p className="text-[var(--brutal-muted)] text-lg leading-relaxed">
                        Applied design thinking methodologies to create a comprehensive AI roadmap. 
                        Translated user insights into actionable development priorities and strategic initiatives.
                      </p>
                    </div>
                    <div className="rounded-lg border border-[var(--brutal-ink)] overflow-hidden h-52">
                      <img 
                        src="/assets/ai-agent-implementation.png" 
                        alt="Implementation and deployment"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Outcomes / Impact */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-[var(--brutal-ink)] mb-12 text-center">Strategic Outcomes</h2>
            
            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div>
                <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-6">Roadmap & Alignment</h3>
                <p className="text-[var(--brutal-muted)] text-lg leading-relaxed mb-6">
                  Created a comprehensive AI roadmap spanning from basic recommendations to sophisticated agentic assistants 
                  and goal-oriented AI systems. Achieved excellent cross-functional alignment around our AI vision.
                </p>
                <p className="text-[var(--brutal-muted)] text-lg leading-relaxed mb-8">
                  The strategic framework was showcased to client advisory boards, demonstrating our forward-thinking 
                  approach to AI integration and establishing thought leadership in the retail planning space.
                </p>


              </div>

              <div>
                {/* Video Placeholder 2 */}
                <Card variant="media" className="brutal-panel mb-6 overflow-hidden">
                  <Components.ImageDisplay
                    src="/assets/llm-strategy.png"
                    alt="AI Assistant Workflow Demo - Guided planning step demonstration"
                    className="w-full h-64 object-cover"
                  />
                </Card>
              </div>
            </div>
          </section>

          {/* Reflection / Lessons */}
          <section className="mb-20">
            <h2 className="text-2xl md:text-4xl font-bold text-[var(--brutal-ink)] mb-12 text-center">Key Learnings</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="brutal-panel p-6">
                <div className="w-12 h-12 brutal-chip bg-[var(--brutal-mustard)] mb-4">
                  <Database weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                </div>
                <h4 className="text-lg md:text-xl font-semibold text-[var(--brutal-ink)] mb-3">The model is not the product knowledge</h4>
                <p className="text-sm md:text-base text-[var(--brutal-muted)] leading-relaxed">
                  The LLM does not know your promo calendars or last week&apos;s inventory rules. That knowledge has to be pulled in at run time. We only fine-tuned when the behavior needed to live in the model. Otherwise the agent had to cite the source, or say it did not have enough to go on.
                </p>
              </Card>

              <Card className="brutal-panel p-6">
                <div className="w-12 h-12 brutal-chip bg-[var(--brutal-kraft)] mb-4">
                  <ShieldCheck weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                </div>
                <h4 className="text-lg md:text-xl font-semibold text-[var(--brutal-ink)] mb-3">Agentic UX is supervised delegation</h4>
                <p className="text-sm md:text-base text-[var(--brutal-muted)] leading-relaxed">
                  Chat is not enough when the system can change a plan. We designed suggest, confirm, and act levels, with clear permission boundaries and a way to pause or undo. Autonomy needs to match the risk. A single &ldquo;AI button&rdquo; does not.
                </p>
              </Card>

              <Card className="brutal-panel p-6">
                <div className="w-12 h-12 brutal-chip bg-[#8fce8f] mb-4">
                  <CheckCircle weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                </div>
                <h4 className="text-lg md:text-xl font-semibold text-[var(--brutal-ink)] mb-3">Groundedness beats confidence theater</h4>
                <p className="text-sm md:text-base text-[var(--brutal-muted)] leading-relaxed">
                  A fluent wrong answer is still a product failure. We pushed to show the source, allow &ldquo;not enough evidence,&rdquo; and keep a human in the loop on high-stakes moves. Evaluation had to ask two questions: did we retrieve the right thing, and did the answer actually help?
                </p>
              </Card>

              <Card className="brutal-panel p-6">
                <div className="w-12 h-12 brutal-chip bg-[var(--brutal-accent)] mb-4">
                  <Stack weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                </div>
                <h4 className="text-lg md:text-xl font-semibold text-[var(--brutal-ink)] mb-3">Design for goals when the stack moves under you</h4>
                <p className="text-sm md:text-base text-[var(--brutal-muted)] leading-relaxed">
                  Models and APIs will keep shifting. What should stay stable is the job: which decisions the agent owns, what it cannot do alone, and how planners check the outcome. Anchoring the concept car in real problems and failure modes kept the strategy useful when the stack moved.
                </p>
              </Card>
            </div>
          </section>



          {/* Footer / Call to Action */}
          <section className="text-center">
            <Card className="brutal-panel">
              <CardContent className="p-12">
                <h2 className="text-3xl font-bold text-[var(--brutal-ink)] mb-6">
                  Ready to Lead AI Strategy Together?
                </h2>
                <p className="text-xl text-[var(--brutal-muted)] mb-8 max-w-3xl mx-auto">
                  Interested in how I can lead UX strategy and design for your AI initiatives? Let's connect and explore 
                  how strategic design thinking can accelerate your AI transformation.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    asChild 
                    variant="outline" 
                    className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] hover:text-[var(--brutal-ink)]"
                  >
                    <Link to="/ProfessionalProjects" className="flex items-center gap-2">
                      View More Projects
                      <ArrowRight size={18} />
                    </Link>
                  </Button>
                  <Button 
                    asChild 
                    size="lg"
                    className="brutal-btn text-[var(--brutal-ink)] border-0 px-8 py-3"
                  >
                    <Link to="/Contact">Get In Touch</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>

      {/* Image Modal */}
      {selectedImage && (
        <Components.ImageModal
          src={selectedImage}
          alt="AI Agent Case Study"
        />
      )}
    </Components.PageContainer>
  )
}